import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import client from "../lib/api";
import { toast } from "sonner";

/**
 * Wave 12 — First Cold Email Wizard
 *
 * Persistent 3-step onboarding wizard that pops up on /app/admin/leads
 * the first time an admin lands there with zero sent emails.
 *
 * Steps:
 *   1) Check your DNS  → auto-hits /admin/leads/dns-check, shows verdict + fix
 *   2) Pick 5 leads    → auto-fetches 5 small-fleet leads via /leads/fmcsa-search
 *   3) Fire warmup     → posts /admin/leads/warmup/start with the 5 leads
 *
 * State persisted server-side via /admin/leads/wizard/state (dismiss/complete
 * flags survive refresh). Never re-shows once completed or after any real
 * cold email has been sent.
 *
 * Wave 13: parent can call `wizardRef.current.replay()` to force-open the
 * wizard even after 100+ sends (clears completed/dismissed via /reset).
 */
const FirstColdEmailWizard = forwardRef(function FirstColdEmailWizard({ onComplete }, ref) {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  // Step 1 state
  const [dns, setDns] = useState(null);

  // Step 2 state
  const [pickedLeads, setPickedLeads] = useState([]);
  const [searchState, setSearchState] = useState("TX");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/admin/leads/wizard/state");
        if (data.should_show) {
          setShow(true);
          setStep(data.step || 1);
        }
      } catch { /* silent — never blocks the page */ }
    })();
  }, []);

  // Expose a replay handler to the parent so a "Replay setup" chip can
  // force the wizard open again from anywhere on the Cold Leads page.
  useImperativeHandle(ref, () => ({
    replay: async () => {
      try {
        await client.post("/admin/leads/wizard/reset");
        setStep(1);
        setDns(null);
        setPickedLeads([]);
        setShow(true);
      } catch (e) {
        toast.error("Failed to replay setup");
      }
    },
  }));

  const persistState = async (patch) => {
    try {
      await client.put("/admin/leads/wizard/state", patch);
    } catch { /* silent */ }
  };

  const runDnsCheck = async () => {
    setBusy(true);
    try {
      const { data } = await client.get("/admin/leads/deliverability/dns");
      setDns(data);
    } catch (e) {
      toast.error("DNS check failed — you can still continue");
      setDns({ error: true });
    } finally {
      setBusy(false);
    }
  };

  const searchLeads = async () => {
    setBusy(true);
    try {
      // Fetch fresh 5 small-fleet leads directly from FMCSA (auto-imports)
      const { data } = await client.post("/admin/leads/fetch-fmcsa", {
        state: searchState,
        min_units: 1,
        max_units: 5,
        require_email: true,
        limit: 5,
      });
      // Pull the freshly imported leads (source=fmcsa) matching the state
      const { data: listData } = await client.get(
        `/admin/leads?source=fmcsa&state=${searchState}&limit=5&sort=recent`
      );
      setPickedLeads((listData.leads || listData.results || []).slice(0, 5));
      toast.success(`Imported ${data.imported || 0} new leads (${data.dupes || 0} duplicates skipped)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lead search failed");
    } finally {
      setBusy(false);
    }
  };

  const fireWarmup = async () => {
    if (pickedLeads.length === 0) {
      toast.error("Pick some leads first");
      return;
    }
    setBusy(true);
    try {
      const lead_ids = pickedLeads.map((l) => l.id || l.dot_number);
      await client.post("/admin/leads/warmup/start", { lead_ids });
      toast.success(`Warmup started for ${pickedLeads.length} leads. Emails will drip over the next 5 business days.`);
      await persistState({ completed: true, step: 3 });
      setShow(false);
      onComplete?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Warmup failed to start");
    } finally {
      setBusy(false);
    }
  };

  const advance = async () => {
    const next = step + 1;
    await persistState({ step: next });
    setStep(next);
  };

  const dismiss = async () => {
    await persistState({ dismissed: true, step });
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      data-testid="first-cold-email-wizard"
    >
      <div className="max-w-2xl w-full bg-neutral-950 border border-white/10 rounded-lg overflow-hidden">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold">
              Guided setup · Step {step} of 3
            </div>
            <div className="text-xl font-semibold text-white mt-1">
              {step === 1 && "Check your sender reputation"}
              {step === 2 && "Pick your first 5 leads"}
              {step === 3 && "Fire your first warmup"}
            </div>
          </div>
          <button
            onClick={dismiss}
            data-testid="wizard-dismiss"
            className="text-neutral-500 hover:text-white text-xs"
          >
            Skip setup
          </button>
        </div>

        {/* Step 1 — DNS check */}
        {step === 1 && (
          <div className="p-6 space-y-4">
            <p className="text-sm text-neutral-400 leading-relaxed">
              Before sending real cold emails, we need to make sure Gmail + Outlook won't route your messages to spam. This checks your <b className="text-white">SPF, DKIM, and DMARC</b> records at your DNS.
            </p>
            {!dns && (
              <button
                onClick={runDnsCheck}
                disabled={busy}
                data-testid="wizard-step1-check"
                className="rounded-full px-5 py-2 text-sm font-semibold bg-[#FF5A1F] hover:bg-[#FF7038] text-black disabled:opacity-50"
              >
                {busy ? "Checking…" : "Run DNS check"}
              </button>
            )}
            {dns && !dns.error && (
              <div className="space-y-2">
                {["spf", "dkim", "dmarc"].map((k) => {
                  const v = dns[k] || {};
                  const ok = (v.status || "").startsWith("pass");
                  const rec = (dns.recommend || {})[k] || {};
                  return (
                    <div
                      key={k}
                      data-testid={`wizard-step1-${k}`}
                      className={`p-3 rounded-md border ${ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-white font-semibold uppercase tracking-wide text-xs">{k}</span>
                        <span className={ok ? "text-emerald-300 text-xs" : "text-amber-300 text-xs"}>
                          {ok ? `✓ ${v.status.toUpperCase()}` : `⚠ ${(v.status || "missing").toUpperCase()}`}
                        </span>
                      </div>
                      {!ok && rec.value && (
                        <div className="mt-2 p-2 rounded bg-black/40 text-[11px] font-mono text-amber-200 break-all">
                          <div className="text-neutral-500 text-[10px] mb-1">{rec.type} · Host: {rec.host}</div>
                          {rec.value}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="text-[11px] text-neutral-500 mt-2">
                  You can continue with warnings and fix DNS later in the Cold Leads page. Overall: <b className="text-white">{dns.overall}</b>
                </div>
              </div>
            )}
            {dns && dns.error && (
              <div className="text-xs text-amber-300">DNS check couldn't complete — continue anyway.</div>
            )}
            <div className="flex justify-end">
              <button
                onClick={advance}
                disabled={busy}
                data-testid="wizard-step1-next"
                className="text-sm px-5 py-2 rounded-full bg-white/[0.05] border border-white/10 text-white hover:border-white/25 disabled:opacity-50"
              >
                Next: pick leads →
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — pick leads */}
        {step === 2 && (
          <div className="p-6 space-y-4">
            <p className="text-sm text-neutral-400 leading-relaxed">
              We'll pull <b className="text-white">5 small-fleet carriers</b> (1-5 trucks) from FMCSA in your state. You can adjust the filter later.
            </p>
            <div className="flex items-center gap-2">
              <label className="text-xs text-neutral-400">State</label>
              <select
                value={searchState}
                onChange={(e) => setSearchState(e.target.value)}
                data-testid="wizard-step2-state"
                className="bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-1.5 px-2 focus:outline-none focus:border-[#FF5A1F]/60"
              >
                {["TX", "CA", "FL", "GA", "OH", "IL", "NC", "PA", "NY", "TN"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <button
                onClick={searchLeads}
                disabled={busy}
                data-testid="wizard-step2-search"
                className="rounded-full px-4 py-1.5 text-xs font-semibold bg-[#FF5A1F] hover:bg-[#FF7038] text-black disabled:opacity-50"
              >
                {busy ? "Searching…" : "Find 5 leads"}
              </button>
            </div>
            {pickedLeads.length > 0 && (
              <div className="space-y-1 max-h-56 overflow-y-auto" data-testid="wizard-step2-results">
                {pickedLeads.map((l, i) => (
                  <div key={l.id || l.dot_number || i} className="p-2 rounded-md border border-white/10 bg-white/[0.02] text-xs">
                    <div className="text-white">{l.dba_name || l.legal_name || "Unknown"}</div>
                    <div className="text-neutral-500">
                      DOT {l.dot_number} · {l.total_trucks || l.power_units || "?"} trucks · {l.state || l.phy_state || "—"}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="text-xs px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 text-neutral-400 hover:text-white"
              >
                ← Back
              </button>
              <button
                onClick={advance}
                disabled={busy || pickedLeads.length === 0}
                data-testid="wizard-step2-next"
                className="text-sm px-5 py-2 rounded-full bg-white/[0.05] border border-white/10 text-white hover:border-white/25 disabled:opacity-50"
              >
                Next: fire warmup →
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — fire warmup */}
        {step === 3 && (
          <div className="p-6 space-y-4">
            <p className="text-sm text-neutral-400 leading-relaxed">
              Ready to fire. We'll queue <b className="text-white">{pickedLeads.length} emails</b> over the next 5 business days (1-2/day) at safe Tue-Thu 9am-2pm ET windows. This protects your sender reputation.
            </p>
            <div className="p-3 rounded-md border border-white/10 bg-white/[0.02]">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Queue preview</div>
              {pickedLeads.map((l, i) => (
                <div key={i} className="text-xs text-neutral-400">
                  Day {i + 1}: {l.dba_name || l.legal_name || "Unknown"}
                </div>
              ))}
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => setStep(2)}
                className="text-xs px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 text-neutral-400 hover:text-white"
              >
                ← Back
              </button>
              <button
                onClick={fireWarmup}
                disabled={busy}
                data-testid="wizard-step3-fire"
                className="rounded-full px-6 py-2 text-sm font-semibold bg-[#FF5A1F] hover:bg-[#FF7038] text-black disabled:opacity-50"
              >
                {busy ? "Starting…" : "🚀 Fire warmup"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default FirstColdEmailWizard;
